import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type {
  TestConnectionRequest,
  TestConnectionResponse,
  UpdateDataSourceRequest,
} from '@innermate/shared';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2, PlugZap, XCircle } from 'lucide-react';
import { useEffect } from 'react';

type PanelBody = TestConnectionRequest | UpdateDataSourceRequest;

interface ConnectionTestPanelProps {
  values: () => PanelBody | null;
  /** create 모드는 전체 필드 필요, edit 모드는 id 기반 서버 병합. */
  mode: 'create' | 'edit';
  dataSourceId?: string;
  /** 결과를 부모에게 전달 (저장 버튼 게이트용). */
  onResult?: (ok: boolean | null) => void;
  /** 값이 바뀌면 이 토큰을 갱신 — 결과 표시가 리셋됨. */
  resetToken?: unknown;
  /** disabled (예: stub kind) 일 때 메시지 표시 */
  disabledReason?: string;
}

export function ConnectionTestPanel({
  values,
  mode,
  dataSourceId,
  onResult,
  resetToken,
  disabledReason,
}: ConnectionTestPanelProps) {
  const mutation = useMutation<TestConnectionResponse, unknown, PanelBody>({
    mutationFn: (body) => {
      if (mode === 'edit') {
        if (!dataSourceId) throw new Error('dataSourceId 가 필요합니다.');
        return api.dataSourceTestConnectionById(dataSourceId, body as UpdateDataSourceRequest);
      }
      return api.dataSourceTestConnection(body as TestConnectionRequest);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetToken 만 trigger 로 사용 (mutation.reset / onResult 는 의존하지 않음)
  useEffect(() => {
    mutation.reset();
    onResult?.(null);
  }, [resetToken]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mutation 결과 변화에만 반응 (onResult 는 의존하지 않음)
  useEffect(() => {
    if (mutation.data) {
      onResult?.(mutation.data.ok);
    } else if (mutation.error) {
      onResult?.(false);
    }
  }, [mutation.data, mutation.error]);

  const onClick = async () => {
    const body = values();
    if (!body) return;
    try {
      await mutation.mutateAsync(body);
    } catch {
      // mutation.error 에 자동 반영됨
    }
  };

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="secondary"
        onClick={onClick}
        disabled={mutation.isPending || !!disabledReason}
      >
        {mutation.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <PlugZap className="size-4" />
        )}
        연결 테스트
      </Button>
      {disabledReason ? <p className="text-xs text-foreground-muted">{disabledReason}</p> : null}
      {mutation.data ? (
        mutation.data.ok ? (
          <p className="inline-flex items-center gap-1.5 text-xs text-[hsl(150_60%_45%)]">
            <CheckCircle2 className="size-3.5" />
            연결 성공 · {mutation.data.latencyMs}ms
            {mutation.data.serverVersion ? (
              <span className="text-foreground-muted">
                · {mutation.data.serverVersion.split('\n')[0]?.slice(0, 80)}
              </span>
            ) : null}
          </p>
        ) : (
          <p className="inline-flex items-center gap-1.5 text-xs text-destructive">
            <XCircle className="size-3.5" />
            {mutation.data.error ?? '연결 실패'}
          </p>
        )
      ) : null}
      {mutation.error ? (
        <p className="text-xs text-destructive">
          {mutation.error instanceof ApiError ? mutation.error.message : '연결 테스트 실패'}
        </p>
      ) : null}
    </div>
  );
}
